import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/brand";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const handleNav = (e, to) => {
    if (to.startsWith("/#")) {
      e.preventDefault();
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/", { state: { scrollTo: id } });
      } else {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "nav-glass shadow-[0_4px_24px_rgba(30,91,184,0.06)]" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
            <img src={BRAND.logo} alt="Innovative Preschool logo" width="48" height="48" className="h-12 w-12 object-contain" />
            <div className="leading-tight">
              <div className="font-heading text-[1.05rem] font-700 text-brand-blue font-bold">Innovative Preschool</div>
              <div className="text-[0.7rem] text-[color:var(--text-secondary)] hidden sm:block">Nepalese Concept Preschool</div>
            </div>
          </Link>

          {/* Desktop */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.id}
                to={l.to}
                onClick={(e) => handleNav(e, l.to)}
                data-testid={`nav-${l.id}`}
                className="px-3 py-2 rounded-full text-[0.92rem] font-semibold text-[color:var(--text-primary)] hover:text-brand-blue hover:bg-light-blue transition"
              >
                {l.label}
              </Link>
            ))}
            <a
              href="/#admissions"
              onClick={(e) => handleNav(e, "/#admissions")}
              className="btn-primary ml-2 !py-2 !px-5 !text-[0.92rem]"
              data-testid="nav-apply-cta"
            >
              Apply Now
            </a>
          </nav>

          {/* Mobile button */}
          <button
            className="lg:hidden p-2 rounded-full text-brand-blue hover:bg-light-blue"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden pb-6 animate-float-up" data-testid="mobile-menu">
            <div className="bg-white rounded-3xl shadow-[0_10px_40px_rgba(30,91,184,0.10)] p-4">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.id}
                  to={l.to}
                  onClick={(e) => handleNav(e, l.to)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="block px-4 py-3 rounded-2xl font-semibold text-[color:var(--text-primary)] hover:text-brand-blue hover:bg-light-blue"
                >
                  {l.label}
                </Link>
              ))}
              <a
                href="/#admissions"
                onClick={(e) => handleNav(e, "/#admissions")}
                className="btn-primary w-full justify-center mt-3"
                data-testid="mobile-apply-cta"
              >
                Apply Now
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
